import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { HiSave, HiX } from "react-icons/hi";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./EditPaymentGateway.scss";

interface PaymentGatewayFormData {
  id: number;
  name: string;
  currencyId: string;
  displayOrder: string;
  isActive: boolean;
  type: number;
  commissionType: number;
  commissionPercent: string;
  commissionValue: string;
  additionalProperties: PaymentGatewayProperty[];
}

interface PaymentGatewayProperty {
  key: string;
  value: string;
}

interface PaymentGatewayFormErrors {
  name?: string;
  currencyCode?: string;
  displayOrder?: string;
  commissionPercent?: string;
  commissionValue?: string;
}

interface CurrencyOption {
  id: number;
  name: string;
  symbol: string;
}

export const EditPaymentGateway: React.FC = () => {
  const { t, i18n } = useTranslation(['paymentGateways', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState<PaymentGatewayFormData>({
    id: parseInt(id || '0'),
    name: "",
    currencyId: "",
    displayOrder: "",
    isActive: true,
    type: 0,
    commissionType: 0,
    commissionPercent: "",
    commissionValue: "",
    additionalProperties: [{ key: "", value: "" }],
  });

  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [fetching, setFetching] = useState(true);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [errors, setErrors] = useState<PaymentGatewayFormErrors>({});

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await adminApi.call('/currency?pageSize=1000000', 'GET');
        if (response.success) {
          setCurrencies(response.data?.items || []);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchPaymentGateway = async () => {
      try {
        const response = await adminApi.call(`/paymentgateway/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('paymentGateways:messages.fetchError'));
          navigate('/payment-gateways');
          return;
        }

        setFormData({
          id: response.data.id,
          name: response.data.name,
          currencyId: response.data.currencyId ? String(response.data.currencyId) : "",
          displayOrder: response.data.displayOrder !== null && response.data.displayOrder !== undefined
            ? String(response.data.displayOrder)
            : "",
          isActive: response.data.isActive,
          type: response.data.type ?? 0,
          commissionType: response.data.commissionType ?? 0,
          commissionPercent: response.data.commissionPercent !== null && response.data.commissionPercent !== undefined
            ? String(response.data.commissionPercent)
            : "",
          commissionValue: response.data.commissionValue !== null && response.data.commissionValue !== undefined
            ? String(response.data.commissionValue)
            : "",
          additionalProperties: parseAdditionalProperties(response.data.additionalPropertiesJson),
        });
      } catch (error) {
        showErrorToast(t('paymentGateways:messages.fetchError'));
        console.error('Error fetching payment gateway:', error);
        navigate('/payment-gateways');
      } finally {
        setFetching(false);
      }
    };

    if (id) {
      fetchPaymentGateway();
    }
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const isCheckbox = (e.target as HTMLInputElement).type === 'checkbox';
    const isNumericSelect = name === 'type' || name === 'commissionType';

    setFormData({
      ...formData,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : isNumericSelect ? parseInt(value) : value,
    });

    // Clear error when user starts typing
    if (errors[name as keyof PaymentGatewayFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const parseAdditionalProperties = (jsonValue?: string): PaymentGatewayProperty[] => {
    if (!jsonValue) {
      return [{ key: "", value: "" }];
    }

    try {
      const parsed = JSON.parse(jsonValue) as Record<string, string>;
      const properties = Object.entries(parsed).map(([key, value]) => ({ key, value: String(value ?? "") }));

      return properties.length > 0 ? properties : [{ key: "", value: "" }];
    } catch {
      return [{ key: "", value: "" }];
    }
  };

  const handlePropertyChange = (index: number, field: keyof PaymentGatewayProperty, value: string) => {
    setFormData(prev => {
      const properties = [...prev.additionalProperties];
      properties[index] = {
        ...properties[index],
        [field]: value,
      };

      return {
        ...prev,
        additionalProperties: properties,
      };
    });
  };

  const addPropertyRow = () => {
    setFormData(prev => ({
      ...prev,
      additionalProperties: [...prev.additionalProperties, { key: "", value: "" }],
    }));
  };

  const removePropertyRow = (index: number) => {
    setFormData(prev => {
      if (prev.additionalProperties.length === 1) {
        return {
          ...prev,
          additionalProperties: [{ key: "", value: "" }],
        };
      }

      return {
        ...prev,
        additionalProperties: prev.additionalProperties.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: PaymentGatewayFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('paymentGateways:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('paymentGateways:validation.nameMinLength');
    } else if (formData.name.length > 100) {
      newErrors.name = t('paymentGateways:validation.nameMaxLength');
    }

    if (!formData.currencyId.trim()) {
      newErrors.currencyCode = t('paymentGateways:validation.currencyCodeRequired');
    }

    if (formData.displayOrder.trim()) {
      const parsedDisplayOrder = Number(formData.displayOrder);
      if (!Number.isInteger(parsedDisplayOrder) || parsedDisplayOrder < 0) {
        newErrors.displayOrder = t('paymentGateways:validation.displayOrderRange');
      }
    }

    const requiresPercent = formData.commissionType === 0 || formData.commissionType === 2;
    const requiresValue = formData.commissionType === 1 || formData.commissionType === 2;

    if (requiresPercent) {
      const parsedPercent = Number(formData.commissionPercent);
      if (Number.isNaN(parsedPercent) || formData.commissionPercent.trim() === "") {
        newErrors.commissionPercent = t('paymentGateways:validation.commissionPercentRequired');
      } else if (parsedPercent < 0 || parsedPercent > 100) {
        newErrors.commissionPercent = t('paymentGateways:validation.commissionPercentRange');
      }
    }

    if (requiresValue) {
      const parsedValue = Number(formData.commissionValue);
      if (Number.isNaN(parsedValue) || formData.commissionValue.trim() === "") {
        newErrors.commissionValue = t('paymentGateways:validation.commissionValueRequired');
      } else if (parsedValue < 0) {
        newErrors.commissionValue = t('paymentGateways:validation.commissionValueMin');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const requiresPercent = formData.commissionType === 0 || formData.commissionType === 2;
      const requiresValue = formData.commissionType === 1 || formData.commissionType === 2;

      const payload = {
        ...formData,
        currencyId: formData.currencyId ? Number(formData.currencyId) : null,
        displayOrder: formData.displayOrder.trim() ? Number(formData.displayOrder) : null,
        commissionPercent: requiresPercent ? Number(formData.commissionPercent) : null,
        commissionValue: requiresValue ? Number(formData.commissionValue) : null,
        additionalProperties: formData.additionalProperties
          .filter(item => item.key.trim())
          .map(item => ({ key: item.key.trim(), value: item.value.trim() })),
      };

      const response = await adminApi.call('/paymentgateway', 'PUT', payload);

      if (!response.success) {
        showErrorToast(response.error || t('paymentGateways:messages.updateError'));
        return;
      }

      if (logoFile) {
        const logoData = new FormData();
        logoData.append('logo', logoFile);
        const logoResponse = await adminApi.upload(`/paymentgateway/${formData.id}/logo`, logoData);
        if (!logoResponse.success) {
          showErrorToast(logoResponse.error || t('paymentGateways:messages.logoUploadError'));
          return;
        }
      }

      showSuccessToast(t('paymentGateways:messages.updateSuccess'));

      // Navigate back to list after successful update
      setTimeout(() => {
        navigate('/payment-gateways');
      }, 1000);

    } catch (error) {
      showErrorToast(t('paymentGateways:messages.updateError'));
      console.error('Error updating payment gateway:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/payment-gateways');
  };

  const breadcrumbs = [
    { label: t('paymentGateways:breadcrumbs.paymentGateways'), path: '/payment-gateways' },
    { label: t('paymentGateways:breadcrumbs.edit') }
  ];

  const showCommissionPercent = formData.commissionType === 0 || formData.commissionType === 2;
  const showCommissionValue = formData.commissionType === 1 || formData.commissionType === 2;

  const headerActions = (
    <div className="header-actions">
      <ActionButton
        variant="secondary"
        icon={HiX}
        onClick={handleCancel}
      >
        {t('common:cancel')}
      </ActionButton>
      <ActionButton
        variant="primary"
        icon={HiSave}
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
      >
        {t('common:save')}
      </ActionButton>
    </div>
  );

  if (fetching) {
    return (
      <PageLayout
        title={t('paymentGateways:editPaymentGateway')}
        subtitle={t('paymentGateways:subtitle')}
        breadcrumbs={breadcrumbs}
        className={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('paymentGateways:editPaymentGateway')}
      subtitle={t('paymentGateways:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12">
                <div className="mb-3">
                  <label htmlFor="isActive" className="form-label">
                    {t('paymentGateways:fields.isActive')}
                  </label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      {formData.isActive ? t('common:active') : t('common:inactive')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <MultiLanguageTextInput
                    label={t('paymentGateways:fields.name')}
                    value={formData.name}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, name: value }));
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: undefined }));
                      }
                    }}
                    placeholder={t('paymentGateways:placeholders.name')}
                    error={errors.name}
                    disabled={loading}
                    required
                    isRTL={isRTL}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="type" className="form-label">
                    {t('paymentGateways:fields.type')}
                  </label>
                  <select
                    className="form-select"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value={0}>{t('paymentGateways:typeOptions.disabled')}</option>
                    <option value={1}>{t('paymentGateways:typeOptions.enabledWhatsapp')}</option>
                    <option value={2}>{t('paymentGateways:typeOptions.enabledOnlinePayment')}</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="currencyCode" className="form-label">
                    {t('paymentGateways:fields.currencyCode')} <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.currencyCode ? 'is-invalid' : ''}`}
                    id="currencyCode"
                    name="currencyId"
                    value={formData.currencyId}
                    onChange={handleInputChange}
                    disabled={currenciesLoading}
                  >
                    <option value="">{t('paymentGateways:placeholders.currencyCode')}</option>
                    {currencies.map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.symbol} - {currency.name}
                      </option>
                    ))}
                  </select>
                  {errors.currencyCode && <div className="invalid-feedback">{errors.currencyCode}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="displayOrder" className="form-label">
                    {t('paymentGateways:fields.displayOrder')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control ${errors.displayOrder ? 'is-invalid' : ''}`}
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    placeholder={t('paymentGateways:placeholders.displayOrder')}
                  />
                  {errors.displayOrder && <div className="invalid-feedback">{errors.displayOrder}</div>}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="logo" className="form-label">
                    {t('paymentGateways:fields.logo')}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="logo"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(event) => setLogoFile(event.target.files?.[0] ?? null)}
                  />
                  <div className="form-text">{t('paymentGateways:placeholders.logo')}</div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="commissionType" className="form-label">
                    {t('paymentGateways:fields.commissionType')}
                  </label>
                  <select
                    className="form-select"
                    id="commissionType"
                    name="commissionType"
                    value={formData.commissionType}
                    onChange={handleInputChange}
                  >
                    <option value={0}>{t('paymentGateways:commissionTypeOptions.percent')}</option>
                    <option value={1}>{t('paymentGateways:commissionTypeOptions.value')}</option>
                    <option value={2}>{t('paymentGateways:commissionTypeOptions.percentAndValue')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              {showCommissionPercent && (
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="commissionPercent" className="form-label">
                      {t('paymentGateways:fields.commissionPercent')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className={`form-control ${errors.commissionPercent ? 'is-invalid' : ''}`}
                      id="commissionPercent"
                      name="commissionPercent"
                      value={formData.commissionPercent}
                      onChange={handleInputChange}
                      placeholder={t('paymentGateways:placeholders.commissionPercent')}
                    />
                    {errors.commissionPercent && <div className="invalid-feedback">{errors.commissionPercent}</div>}
                  </div>
                </div>
              )}

              {showCommissionValue && (
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="commissionValue" className="form-label">
                      {t('paymentGateways:fields.commissionValue')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`form-control ${errors.commissionValue ? 'is-invalid' : ''}`}
                      id="commissionValue"
                      name="commissionValue"
                      value={formData.commissionValue}
                      onChange={handleInputChange}
                      placeholder={t('paymentGateways:placeholders.commissionValue')}
                    />
                    {errors.commissionValue && <div className="invalid-feedback">{errors.commissionValue}</div>}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">{t('paymentGateways:fields.additionalProperties')}</label>
              {formData.additionalProperties.map((property, index) => (
                <div className="row g-2 mb-2" key={index}>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      value={property.key}
                      onChange={(e) => handlePropertyChange(index, 'key', e.target.value)}
                      placeholder={t('paymentGateways:placeholders.additionalPropertyKey')}
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      value={property.value}
                      onChange={(e) => handlePropertyChange(index, 'value', e.target.value)}
                      placeholder={t('paymentGateways:placeholders.additionalPropertyValue')}
                    />
                  </div>
                  <div className="col-md-2 d-grid">
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removePropertyRow(index)}
                    >
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={addPropertyRow}
              >
                {t('paymentGateways:buttons.addProperty')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};
